import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { NavPillsBoxContainer } from 'components/common/Styles';
import { Card, NavPills, Typography } from '@totalsoft/rocket-ui';
import CalculateIcon from '@mui/icons-material/Calculate';
import FormulaField from 'features/common/components/formula-field';
import { get, set } from '@totalsoft/change-tracking-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'hooks/apollo';
import { GenericFilterContext } from 'features/common/hooks/useGenericFilterData';
import { GENERIC_FILTER_PARAMS_QUERY } from 'features/genericFilters/queries';
import { FormulaFieldContainer, FormulaParamsCollapseCard } from 'components/common/Styles';
import FlexBox from 'components/common/FlexBox';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

const LeasingServiceTypeFormulas = ({ lens, disabled }) => {
  const { t } = useTranslation();

  const [active, setActive] = useState(0);

  return (
    <Card title={t('LeasingServiceType.Formulas')} icon={CalculateIcon} iconColor='primary'>
      <NavPillsBoxContainer panelTopMargin={1} panelOverflow='visible'>
        <NavPills
          fullWidth
          active={active}
          onChange={(_event, active) => {
            setActive(active);
          }}
          tabs={[
            {
              label: t('LeasingServiceType.AmountFormula'),
              content: (
                <FormulaFieldContainer>
                  <FormulaField value={lens.amountFormula |> get} onChange={lens.amountFormula |> set} disabled={disabled} />
                </FormulaFieldContainer>
              )
            },
            {
              label: t('LeasingServiceType.InterestAmountFormula'),
              content: (
                <FormulaFieldContainer>
                  <FormulaField
                    value={lens.interestAmountFormula |> get}
                    onChange={lens.interestAmountFormula |> set}
                    disabled={disabled}
                  />
                </FormulaFieldContainer>
              )
            },
            {
              label: t('LeasingServiceType.VatAmountFormula'),
              content: (
                <FormulaFieldContainer>
                  <FormulaField value={lens.vatAmountFormula |> get} onChange={lens.vatAmountFormula |> set} disabled={disabled} />
                </FormulaFieldContainer>
              )
            },
            {
              label: t('LeasingServiceType.InitializationFormula'),
              content: (
                <FormulaFieldContainer>
                  <FormulaField
                    value={lens.initializationFormula |> get}
                    onChange={lens.initializationFormula |> set}
                    disabled={disabled}
                  />
                </FormulaFieldContainer>
              )
            },
            {
              label: t('LeasingServiceType.ServiceFormulas'),
              content: <ServiceFormulas lens={lens} disabled={disabled} />
            }
          ]}
        />
      </NavPillsBoxContainer>
    </Card>
  );
};

const ServiceFormulas = ({ lens, disabled }) => {
  const { t } = useTranslation();

  const [active, setActive] = useState(0);

  const { data: formulaParamsData } = useQuery(GENERIC_FILTER_PARAMS_QUERY, {
    variables: {
      context: GenericFilterContext.FinancialService
    }
  });

  const formulaParams = useMemo(() => formulaParamsData?.genericFilterParams || [], [formulaParamsData]);

  return (
    <FlexBox direction='column' gap={0.5} mt={2}>
      <Box px={1}>
        <FormulaParamsCollapseCard
          subheader={
            <Typography color='textPrimary' fontWeight='bold' variant='body2'>
              {t('LeasingServiceType.ServiceFormulaParameters')}
            </Typography>
          }
          toggleOnHeaderClick
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>{t('Common.GenericFilterParam.Code')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('Common.GenericFilterParam.Name')}</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formulaParams.length > 0 &&
                formulaParams.map(param => (
                  <TableRow key={param.paramId}>
                    <TableCell>{param.paramCode || '-'}</TableCell>
                    <TableCell>{param.paramName || '-'}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </FormulaParamsCollapseCard>
      </Box>

      <NavPillsBoxContainer panelTopMargin={1} panelOverflow='visible'>
        <NavPills
          fullWidth
          active={active}
          onChange={(_event, active) => {
            setActive(active);
          }}
          tabs={[
            {
              label: t('LeasingServiceType.DefaultAmountFormula'),
              content: (
                <FormulaFieldContainer>
                  <FormulaField value={lens.defaultAmountFormula |> get} onChange={lens.defaultAmountFormula |> set} disabled={disabled} />
                </FormulaFieldContainer>
              )
            },
            {
              label: t('LeasingServiceType.DefaultPercentFormula'),
              content: (
                <FormulaFieldContainer>
                  <FormulaField
                    value={lens.defaultPercentFormula |> get}
                    onChange={lens.defaultPercentFormula |> set}
                    disabled={disabled}
                  />
                </FormulaFieldContainer>
              )
            }
          ]}
        />
      </NavPillsBoxContainer>
    </FlexBox>
  );
};

ServiceFormulas.propTypes = {
  lens: PropTypes.object,
  disabled: PropTypes.bool
};

LeasingServiceTypeFormulas.propTypes = {
  lens: PropTypes.object,
  disabled: PropTypes.bool
};

export default LeasingServiceTypeFormulas;
